import { environment } from '@/environment';
import { firebaseUtils } from '@/utils';

export enum ServiceName {
  UserState = 'userStateService',
  UserManagement = 'UserManagementService',
  FeatureFlag = 'featureFlagService',
  Premium = 'premiumService',
  PremiumHub = 'premiumHubService',
  WebHistory = 'webHistoryService',
  Statistics = 'statisticsService',
  Autocomplete = 'autocompleteService',
}

const SERVICE_URLS: Record<ServiceName, string> = {
  [ServiceName.UserState]: environment.userStateApiBaseUrl,
  [ServiceName.UserManagement]: environment.UserManagementApiBaseUrl,
  [ServiceName.FeatureFlag]: environment.featureFlagApiBaseUrl,
  [ServiceName.Premium]: environment.premiumServiceApiBaseUrl,
  [ServiceName.PremiumHub]: environment.premiumHubServiceApiBaseUrl,
  [ServiceName.WebHistory]: environment.webHistoryServiceApiBaseUrl,
  [ServiceName.Statistics]: environment.statisticsServiceApiBaseUrl,
  [ServiceName.Autocomplete]: environment.autocompleteServiceApiBaseUrl,
};

class Api {
  public serviceUrls = SERVICE_URLS;
  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async getToken(): Promise<string> {
    return (await firebaseUtils.getUserToken()) || '';
  }

  private getBaseUrl(serviceName: ServiceName): string {
    return this.serviceUrls[serviceName] || '';
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        return response.text() as unknown as T;
      }
    } else {
      const errorBody = await response.json().catch(() => ({}));
      const error = new Error(errorBody.message || 'An error occurred');
      (error as any).status = response.status;
      throw error;
    }
  }

  public async httpGet<T>(
    serviceName: ServiceName,
    url: string,
    customHeaders?: Record<string, string>
  ): Promise<T> {
    const headers = await this.getHeaders();
    const baseUrl = this.getBaseUrl(serviceName);
    const response = await fetch(`${baseUrl}${url}`, {
      method: 'GET',
      headers: { ...headers, ...customHeaders },
    });
    return this.handleResponse<T>(response);
  }

  public async httpPost<T>(
    serviceName: ServiceName,
    url: string,
    body?: any
  ): Promise<T> {
    const headers = await this.getHeaders();
    const baseUrl = this.getBaseUrl(serviceName);
    const response = await fetch(`${baseUrl}${url}`, {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse<T>(response);
  }

  public async httpPatch<T>(
    serviceName: ServiceName,
    url: string,
    body?: any
  ): Promise<T> {
    const headers = await this.getHeaders();
    const baseUrl = this.getBaseUrl(serviceName);
    const response = await fetch(`${baseUrl}${url}`, {
      method: 'PATCH',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse<T>(response);
  }

  public async httpPut<T>(
    serviceName: ServiceName,
    url: string,
    body?: any
  ): Promise<T> {
    const headers = await this.getHeaders();
    const baseUrl = this.getBaseUrl(serviceName);
    const response = await fetch(`${baseUrl}${url}`, {
      method: 'PUT',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse<T>(response);
  }

  public async httpDelete<T>(
    serviceName: ServiceName,
    url: string
  ): Promise<T> {
    const headers = await this.getHeaders();
    const baseUrl = this.getBaseUrl(serviceName);
    const response = await fetch(`${baseUrl}${url}`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse<T>(response);
  }
}

export const api = new Api();
